// @PACKAGE-INFO
// http://react-component.github.io/time-picker/

// React
import React, { useRef, useState } from 'react';

// Utils
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Components
import TimePicker from 'rc-time-picker';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import withStyles from '@material-ui/core/styles/withStyles';
import { keyEmptyCheck } from 'aurora-utils/utils';
import { ADGMImage } from 'aurora-components';

// styles
// Please don't change style order
import 'rc-time-picker/assets/index.css';
import styles from './styles';

/**
  ADGMTimePicker is a reusable time picker component built with rc-time-picker.
  It includes additional features and styles, and can be used anywhere in the application.
  
  @param { moment } value - The current time value.
  @param { function } onChange - Function to handle changes to the time value.
  @param { string } name - The name of the time picker.
  @param { string } label - The label for the time picker.
  @param { string } format - The format for the time value.
  @param { object } classes - The classes applied to the component for styling.
  @param { bool } required - Whether the time picker is required.
  @param { string } placement - The placement of the time picker popup.
  @returns { node } - A TimePicker component with additional features and custom styling.
*/

const ADGMTimePicker = ({ label, classes, required, placement, use12Hours, ...props }) => {
  const dariTimeRefWrapper = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <FormGroup className={classes.dateTimeWapper} ref={dariTimeRefWrapper}>
        {label && (
          <FormLabel className={classes.label} required={required}>
            {keyEmptyCheck(label)}
          </FormLabel>
        )}

        <TimePicker
          showSecond={false}
          className={classNames(classes.timePicker, isOpen && classes.pickerIsOpen)}
          popupClassName={classes.timePickerPopup}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          popupStyle={{
            width: 'max-content',
          }}
          placement={props.placement}
          use12Hours={use12Hours}
          inputIcon={<ADGMImage name='timePicker-icon.svg' alt='time-picker-icon' className={classes.timePickerIcon} />}
          clearIcon={<ADGMImage name='clear-icon.svg' alt='clear-field-icon' className={classes.clearTimeIcon} />}
          {...props}
        />
      </FormGroup>
    </>
  );
};

ADGMTimePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  label: PropTypes.string,
  format: PropTypes.string,
  classes: PropTypes.object.isRequired,
  required: PropTypes.bool,
  placement: PropTypes.string,
  use12Hours: PropTypes.bool,
};

ADGMTimePicker.defaultProps = {
  label: undefined,
  format: 'hh:mm A',
  required: false,
  placement: 'bottomLeft',
  use12Hours: true,
};

export default withStyles(styles)(ADGMTimePicker);
